import { FileUp, Loader2, Paperclip, Plus, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { hojeEmTexto, type DadosDaNota } from "@bookdental/schemas";
import { campo, rotulo } from "../estilos";
import { useArquivoDaNota, type ArquivoEnviado } from "../notas/use-arquivo-da-nota";

export type NotaDoLancamentoValor = {
  numero: string;
  serie: string | null;
  chaveDeAcesso: string | null;
  emitidaEm: string;
  arquivos: ArquivoEnviado[];
  lidoDoXml: boolean;
};

export const notaVazia = (): NotaDoLancamentoValor => ({ numero: "", serie: null, chaveDeAcesso: null, emitidaEm: hojeEmTexto(), arquivos: [], lidoDoXml: false });

/**
 * A nota fiscal registrada junto com o lançamento: número e arquivo. O XML preenche a nota e
 * devolve os dados (`onLido`) pro lançamento usar — valor, mês, fornecedor e descrição.
 */
export function NotaDoLancamento({
  nota,
  onChange,
  onLido,
}: {
  nota: NotaDoLancamentoValor | null;
  onChange: (nota: NotaDoLancamentoValor | null) => void;
  onLido: (dados: DadosDaNota) => void;
}) {
  const { enviar, enviando } = useArquivoDaNota();

  if (!nota) {
    return (
      <button
        type="button"
        onClick={() => onChange(notaVazia())}
        className="inline-flex items-center gap-1.5 self-start text-sm font-semibold text-brand-700 hover:underline dark:text-primary"
      >
        <Plus className="h-4 w-4" /> Nota fiscal (opcional)
      </button>
    );
  }

  async function anexar(lista: FileList | null) {
    let atual = nota!;
    for (const f of Array.from(lista ?? [])) {
      try {
        const { enviado, dados } = await enviar(f);
        atual = { ...atual, arquivos: [...atual.arquivos, enviado] };
        if (dados) {
          atual = {
            ...atual,
            numero: dados.numero ?? atual.numero,
            serie: dados.serie ?? atual.serie,
            chaveDeAcesso: dados.chaveDeAcesso ?? atual.chaveDeAcesso,
            emitidaEm: dados.emitidaEm ?? atual.emitidaEm,
            lidoDoXml: true,
          };
          onLido(dados);
        }
        onChange(atual);
      } catch (e) {
        toast.error((e as Error).message);
      }
    }
  }

  return (
    <div className="space-y-2 rounded-lg border border-zinc-200 p-3 dark:border-border">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-zinc-800 dark:text-foreground">Nota fiscal</span>
        <button type="button" onClick={() => onChange(null)} aria-label="Tirar a nota" className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-muted">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-zinc-300 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted">
        {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
        Anexar XML, PDF ou foto da nota
        <input type="file" multiple accept=".xml,.pdf,.jpg,.jpeg,.png" className="sr-only" onChange={(e) => (anexar(e.target.files), (e.target.value = ""))} />
      </label>
      {nota.lidoDoXml && (
        <p className="flex items-center gap-1.5 text-xs text-brand-700 dark:text-primary">
          <Sparkles className="h-3.5 w-3.5" /> Dados lidos do XML. Confira antes de salvar.
        </p>
      )}
      {nota.arquivos.map((a) => (
        <p key={a.fileId} className="flex items-center gap-1.5 truncate text-xs text-zinc-600 dark:text-muted-foreground">
          <Paperclip className="h-3.5 w-3.5 shrink-0" /> {a.nome}
        </p>
      ))}
      <div className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className={rotulo}>Número da nota</span>
          <input value={nota.numero} onChange={(e) => onChange({ ...nota, numero: e.target.value })} className={campo} />
        </label>
        <label>
          <span className={rotulo}>Data da nota</span>
          <input type="date" value={nota.emitidaEm} onChange={(e) => onChange({ ...nota, emitidaEm: e.target.value })} className={campo} />
        </label>
      </div>
    </div>
  );
}
